import { FC, RefObject } from 'react';
import {
    Button,
    makeStyles,
    Drawer,
    DrawerBody,
    DrawerHeader,
    DrawerHeaderTitle,
    useRestoreFocusSource,
    tokens,
} from '@fluentui/react-components';
import { AgentLog } from '../../libs/services/AgentLogService';
import { AgentLogCard } from './AgentLogCard';
import { Breakpoints, customTokens } from '../../styles';
import { DismissCircleRegular } from '@fluentui/react-icons';

const useStyles = makeStyles({
    drawer: {
        maxWidth: '600px',
        width: '420px',
        background: customTokens.colorChatRoomBackground,
        ...Breakpoints.small({
            maxWidth: '100%',
        }),
    },
    title: {
        marginBottom: '16px',
        color: customTokens.colorCustomText,
        fontSize: '18px',
        fontWeight: '600',
    },
    scrollContainer: {
        overflowY: 'auto',
        flex: 1,
        marginTop: '16px',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
    },
    closeButton: {
        borderRadius: tokens.borderRadiusMedium,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
            border: `1px solid ${tokens.colorNeutralStroke1}`,
        },
    },
});

export const AgentLogsPanel: FC<{
    agentLogs: AgentLog[];
    agentLogsEndRef: RefObject<HTMLDivElement>;
    onClose: () => void;
    isOpen: boolean;
}> = ({ agentLogs, agentLogsEndRef, onClose, isOpen }) => {
    const classes = useStyles();

    // Drawer focus restoration attributes
    const restoreFocusSourceAttributes = useRestoreFocusSource();

    return (
        <Drawer
            {...restoreFocusSourceAttributes}
            type="overlay"
            position="end"
            separator
            open={isOpen}
            onOpenChange={(_, { open }) => {
                if (!open) onClose();
            }}
            className={classes.drawer}
        >
            <DrawerHeader>
                <DrawerHeaderTitle
                    action={
                        <Button
                            appearance="subtle"
                            aria-label="Close"
                            icon={<DismissCircleRegular />}
                            onClick={onClose}
                        />
                    }
                >
                    Agent Logs
                </DrawerHeaderTitle>
            </DrawerHeader>

            <DrawerBody>
                <div className={classes.scrollContainer} ref={agentLogsEndRef}>
                    {(Array.isArray(agentLogs) ? agentLogs : []).map((log, i) => {
                        let tokenUsage: { Input: number; Output: number; Total: number } | undefined;
                        if (log.tokenUsage) {
                            try {
                                tokenUsage = JSON.parse(log.tokenUsage) as {
                                    Input: number;
                                    Output: number;
                                    Total: number;
                                };
                            } catch {
                                tokenUsage = undefined;
                            }
                        } else {
                            tokenUsage = undefined;
                        }
                        return (
                            <AgentLogCard
                                log={{
                                    agentName: log.agentName,
                                    content: log.content,
                                    timestamp: log.timestamp,
                                    duration: log.duration,
                                    agentLogType: log.agentLogType,
                                    ...(tokenUsage !== undefined ? { tokenUsage } : {}),
                                }}
                                key={i}
                            />
                        );
                    })}
                </div>
            </DrawerBody>
        </Drawer>
    );
};
